/**
 * Helpers for the AI audience option of the ad campaign wizard.
 *
 * Wizard state used here lives in the Sales Funnel Setup step data:
 *   audienceSelections            stage id -> audience ids (an AI audience is identified by its hypothesis id)
 *   hypothesisAudiences           hypothesis id -> { id, kind, label, catalogueAudienceId, audienceMode, interests, ... }
 *   audienceBuilder               { mode: 'ai' | 'manual', advanced (existing audiences run next to the AI audiences), acquisitionBudget, requestedPersonas, stashedAcquisition, set }
 */

export const AUDIENCE_KIND_LABELS = {
    advantage_broad: 'Advantage+ broad',
    advantage_suggested: 'Advantage+ with suggestions',
    manual_broad: 'Broad',
    interest_cluster: 'Interest group',
    persona_cluster: 'Persona',
    lookalike: 'Lookalike',
}

export const MAX_REQUESTED_PERSONAS = 3

/** Campaign objectives without AI audiences (not even the broad Advantage+ audience). */
export const AI_AUDIENCE_EXCLUDED_OBJECTIVES = ['awareness']

export const isAiAudienceObjective = (objective) =>
    !AI_AUDIENCE_EXCLUDED_OBJECTIVES.includes(String(objective || '').toLowerCase())

export const getAudienceKindLabel = (kind) => AUDIENCE_KIND_LABELS[kind] || 'AI audience'

/** Advantage+ audience with one persona's interests sent as suggestions. */
export const SUGGESTED_AUDIENCE_KIND = 'advantage_suggested'

export const isSuggestedAudienceKind = (kind) => kind === SUGGESTED_AUDIENCE_KIND

export const SUGGESTIONS_NOTE = 'Meta may go beyond these interests; this is not an interest test.'

/** Names of the classes of behaviours and demographics an AI audience may carry next to its interests. */
export const TARGETING_OPTION_CLASS_LABELS = {
    behaviors: 'Behaviour',
    industries: 'Industry',
    work_positions: 'Job title',
    education_statuses: 'Education level',
}

/** "Job title: Accountant"; an option of an unknown class is shown with its name only. */
export const describeTargetingOption = (option) => {
    const classLabel = TARGETING_OPTION_CLASS_LABELS[option?.optionClass]
    return classLabel ? `${classLabel}: ${option.name}` : String(option?.name ?? '')
}

/** Behaviours and demographics of a hypothesis as returned by the server, for the wizard state. */
export const toTargetingOptions = (options) =>
    (Array.isArray(options) ? options : [])
        .filter((option) => option && option.id != null && option.name)
        .map((option) => ({ optionClass: option.option_class, id: String(option.id), name: option.name }))

/** `{ targetingOptions }` when a hypothesis has behaviours or demographics, else nothing. */
const withTargetingOptions = (options) => {
    const targetingOptions = toTargetingOptions(options)
    return targetingOptions.length > 0 ? { targetingOptions } : {}
}

/** Gate summary when the single ad set uses the top persona's interests as Advantage+ suggestions. */
export const SUGGESTED_SINGLE_AD_SET_MESSAGE =
    "Your daily acquisition budget supports one audience, so one Advantage+ audience is planned with the top persona's interests as suggestions. Increase the daily budget to test personas as separate audiences."

export const GATE_REASON_MESSAGES = {
    budget_too_low:
        'With this budget only one broad audience can get enough results, so no persona audiences are planned. Increase the daily budget to unlock more.',
    special_ad_category: 'Your ad account has a special ad category, so only the broad Advantage+ audience is offered.',
    objective_not_eligible:
        'Persona audiences are not offered for this campaign objective yet. A broad Advantage+ audience is planned.',
    invalid_budget: 'Enter a valid daily acquisition budget to plan audiences.',
    insufficient_business_details:
        'Your business profile does not have enough detail to build personas. Add a purpose and summary to the business, then regenerate. A broad Advantage+ audience is planned for now.',
    generation_unavailable:
        'AI suggestions are not available right now, so a broad Advantage+ audience is planned. You can regenerate later.',
    no_suitable_personas:
        'None of the suggested personas passed the targeting checks, so a broad Advantage+ audience is planned.',
    targeting_check_unavailable:
        'The targeting options could not be checked right now, so a broad Advantage+ audience is planned. You can regenerate later.',
    no_validated_interests:
        'None of the suggested interests are currently offered, so a broad Advantage+ audience is planned.',
}

export const PLAN_WARNING_MESSAGES = {
    may_not_exit_learning:
        'This is more personas than the budget supports. Each audience may not get enough results to finish learning.',
    broad_below_learning_budget:
        'The budget is low for the broad audience to finish learning. Results may build up slowly.',
    below_min_per_ad_set: 'Some audiences would get less than the minimum daily budget of your ad account.',
}

const GENERIC_MESSAGE =
    'Something went wrong while preparing AI audiences. Please try again, or use the existing audiences.'

export const ERROR_CODE_MESSAGES = {
    UNAUTHENTICATED: 'Your session has expired. Please sign in again.',
    FORBIDDEN: 'You do not have access to AI audiences for this business and ad account.',
    AUDIENCE_BUILDER_DISABLED: 'AI audiences are not available for this business.',
    PUBLISHER_NOT_CONNECTED:
        'Your ad account connection needs to be renewed. Reconnect it in Media Settings and try again.',
    BAD_USER_INPUT: 'Check the daily budget and campaign details, then try again.',
    AUDIENCE_HYPOTHESES_FAILED:
        'AI audiences could not be prepared right now. Please try again, or use the existing audiences.',
    INVALID_AUDIENCE_HYPOTHESIS:
        'One of the AI audiences is no longer available. Go back to Sales Funnel Setup and regenerate the AI audiences.',
    AUDIENCE_BUILDER_OBJECTIVE_NOT_SUPPORTED: 'AI audiences are not available for this campaign objective.',
    AUDIENCE_HYPOTHESES_RATE_LIMITED:
        'AI audiences were requested too often. Please wait a few minutes and try again, or use the existing audiences.',
    INVALID_ACCOUNT_AUDIENCE:
        'One of the audiences from your ad account can no longer be used. Go back to Sales Funnel Setup, fetch your audiences again and review the selection.',
}

/** Codes returned when a launch request carries a bad audience hypothesis or ad account audience. */
export const LAUNCH_ERROR_CODES = ['INVALID_AUDIENCE_HYPOTHESIS', 'AUDIENCE_BUILDER_DISABLED', 'INVALID_ACCOUNT_AUDIENCE']

export const getErrorCode = (error) => error?.graphQLErrors?.[0]?.extensions?.code || null

/** Maps an Apollo error to a fixed message. The server text is never shown. */
export const mapAudienceBuilderError = (error) => {
    const code = getErrorCode(error)
    if (code && ERROR_CODE_MESSAGES[code]) return ERROR_CODE_MESSAGES[code]
    if (error?.networkError) return 'AI audiences could not be reached. Check your connection and try again.'
    return GENERIC_MESSAGE
}

/** Message for a launch error caused by AI audiences, or null when the error is something else. */
export const getLaunchAudienceErrorMessage = (error) => {
    const code = getErrorCode(error)
    return LAUNCH_ERROR_CODES.includes(code) ? ERROR_CODE_MESSAGES[code] : null
}

/** Gate summary when personas were planned but none is offered. */
export const BROAD_ONLY_MESSAGE = 'Only the broad Advantage+ audience will run.'

/**
 * Number of generated personas the server left out: `discarded_persona_count` in the server result,
 * `discardedPersonaCount` in the set stored in the wizard.
 */
const discardedPersonaCountOf = (set) => Number(set.discarded_persona_count ?? set.discardedPersonaCount) || 0

/** Warning text for a plan warning key. The over-budget persona warning names the chosen and supported counts. */
const describePlanWarning = (key, plan) => {
    const chosen = plan.persona_count
    const supported = plan.recommended_persona_count
    if (key === 'may_not_exit_learning' && Number.isInteger(chosen) && Number.isInteger(supported)) {
        return `You chose ${chosen} ${chosen === 1 ? 'persona' : 'personas'}, but this budget supports ${supported}. Each audience may get too few results to leave Meta's learning phase. Raise the daily budget or lower the count.`
    }
    return PLAN_WARNING_MESSAGES[key]
}

/** Plain-language gate result for a generated set, as returned by the server or as stored in the wizard. */
export const describeBudgetGate = (set) => {
    if (!set) return { summary: '', details: [], warnings: [] }
    const plan = set.plan || {}
    const personaCount = plan.persona_count || 0
    // Personas were planned, but none of them is offered: only the broad audience runs.
    const personasLeftOut = set.status === 'broad_only' && personaCount > 0

    const recommendedCount = plan.recommended_persona_count ?? personaCount
    const personaNoun = (count) => (count === 1 ? 'persona audience' : 'persona audiences')

    let summary
    if (personasLeftOut) {
        summary = [BROAD_ONLY_MESSAGE, GATE_REASON_MESSAGES[set.reason]].filter(Boolean).join(' ')
    } else if (personaCount > recommendedCount) {
        // An advanced override above what the budget supports
        summary = `You chose ${personaCount} ${personaNoun(personaCount)}. Your daily acquisition budget supports one broad audience plus ${recommendedCount} ${personaNoun(recommendedCount)}.`
    } else if (personaCount > 0) {
        summary = `Your daily acquisition budget supports one broad audience plus ${personaCount} ${personaNoun(personaCount)}.`
    } else if (plan.mode === 'broad_suggested') {
        summary = SUGGESTED_SINGLE_AD_SET_MESSAGE
    } else {
        summary = GATE_REASON_MESSAGES[set.reason] || GATE_REASON_MESSAGES[plan.reason] || ''
    }

    const details = []
    if (personasLeftOut) {
        details.push(
            `Your daily acquisition budget supports one broad audience plus ${recommendedCount} ${personaNoun(recommendedCount)}, but no persona audience could be offered.`,
        )
    }
    const count = discardedPersonaCountOf(set)
    if (count > 0) {
        details.push(
            `${count} suggested ${count === 1 ? 'persona was' : 'personas were'} left out because ${
                count === 1 ? 'it' : 'they'
            } did not pass the targeting checks.`,
        )
    }
    if (personaCount > 0 && !personasLeftOut && set.reason && GATE_REASON_MESSAGES[set.reason])
        details.push(GATE_REASON_MESSAGES[set.reason])

    const warnings = (plan.warnings || []).map((key) => describePlanWarning(key, plan)).filter(Boolean)
    return { summary, details, warnings }
}

const toArray = (value) => (Array.isArray(value) ? value : [])

/**
 * Compares the Budget step amounts of the selected AI audiences with the plan they were built for.
 * `budgets` is the Budget step state ({ budgetType, audienceSelections, manualBudgets }, major units); with one
 * campaign budget it is { budgetType: 'advantage_leads', aiShare } (minor units).
 * Returns { total, existingCount, existingTotal, planned, required, belowRequired, differsFromPlan } with amounts in minor
 * units. `total` is the AI audiences' amount; the existing audiences are counted apart (not with one campaign budget).
 */
export const compareAiAudienceBudgets = (salesFunnelData, budgets) => {
    const builder = getBuilderState(salesFunnelData)
    const set = builder.set
    if (builder.mode !== 'ai' || !set || !budgets) return null

    const map = salesFunnelData?.hypothesisAudiences || {}
    const ids = toArray(salesFunnelData?.audienceSelections?.acquisition).filter((id) => map[id])
    if (ids.length === 0) return null

    // One campaign budget (Advantage+ leads or the plain campaign budget): the AI audiences count on their share of it (`aiShare`, minor units)
    if (budgets.budgetType === 'advantage_leads' || budgets.budgetType === 'campaign_budget') {
        const total = Math.max(Number(budgets.aiShare) || 0, 0)
        const planned = Number(set.inputs?.acquisitionBudget) || 0
        const required = Number(set.plan?.required_per_ad_set) || 0
        return {
            total,
            planned,
            required,
            belowRequired: required > 0 && Math.floor(total / ids.length) < required,
            differsFromPlan: planned > 0 && Math.abs(total - planned) >= 100,
        }
    }

    const source =
        budgets.budgetType === 'smart'
            ? budgets.audienceSelections?.acquisition?.audiences
            : budgets.manualBudgets?.acquisition?.audiences
    const amounts = ids.map((id) => Math.round((Number.parseFloat(source?.[id]?.amount) || 0) * 100))
    if (amounts.every((amount) => amount === 0)) return null

    const total = amounts.reduce((sum, amount) => sum + amount, 0)
    const planned = Number(set.inputs?.acquisitionBudget) || 0
    const required = Number(set.plan?.required_per_ad_set) || 0
    const existingIds = toArray(salesFunnelData?.audienceSelections?.acquisition).filter((id) => !map[id])
    const existingTotal = existingIds.reduce(
        (sum, id) => sum + Math.round((Number.parseFloat(source?.[id]?.amount) || 0) * 100),
        0,
    )
    return {
        total,
        existingCount: existingIds.length,
        existingTotal,
        planned,
        required,
        belowRequired: required > 0 && amounts.some((amount) => amount < required),
        differsFromPlan: planned > 0 && Math.abs(total - planned) >= 100,
    }
}

export const getBuilderState = (data) => ({
    mode: 'manual',
    advanced: false,
    acquisitionBudget: '',
    requestedPersonas: null,
    stashedAcquisition: [],
    set: null,
    ...(data?.audienceBuilder || {}),
})

export const getHypothesisMap = (data) => data?.hypothesisAudiences || {}

const withoutHypothesisIds = (ids, map) => toArray(ids).filter((id) => !map[id])

const setAcquisition = (data, acquisition) => ({
    ...data,
    audienceSelections: { ...(data?.audienceSelections || {}), acquisition },
})

/** Minor currency units (cents), the unit of ad set budgets. */
export const toMinorUnits = (amount) => Math.round(Number(amount) * 100)

export const parseBudgetAmount = (value) => {
    const amount = Number.parseFloat(value)
    return Number.isFinite(amount) && amount > 0 ? amount : null
}

export const buildGenerationVariables = ({
    campaignId,
    publisherId,
    objective,
    location,
    acquisitionBudget,
    requestedPersonas,
    regenerate,
}) => {
    const variables = {
        campaignId,
        publisherId,
        objective: String(objective || '').toLowerCase(),
        location,
        acquisitionBudget: toMinorUnits(acquisitionBudget),
    }
    if (requestedPersonas !== null && requestedPersonas !== undefined) variables.requestedPersonas = requestedPersonas
    if (regenerate) variables.regenerate = true
    return variables
}

/**
 * Inputs a generated set depends on, to detect a set that no longer matches the wizard. `basisHash` is what the
 * audience insight was when the set was built (its hash, "none" when no insight was used, null while the feature is
 * off); it is left out while it is not known yet.
 */
export const buildSetInputs = ({
    publisherId,
    objective,
    location,
    acquisitionBudget,
    requestedPersonas,
    basisHash,
}) => ({
    publisherId: publisherId || null,
    objective: String(objective || '').toLowerCase(),
    location: location || null,
    acquisitionBudget: toMinorUnits(acquisitionBudget),
    requestedPersonas: requestedPersonas ?? null,
    ...(basisHash !== undefined ? { basisHash } : {}),
})

/**
 * Sets saved before the persona count or the audience insight was part of the inputs have no such key and are not
 * judged by it; the insight is not judged while the current one is not known yet.
 */
export const isSetStale = (set, currentInputs) => {
    if (!set?.inputs) return false
    return Object.keys(set.inputs).some((key) => {
        if (key === 'basisHash' && currentInputs.basisHash === undefined) return false
        return set.inputs[key] !== currentInputs[key]
    })
}

/** Enters AI mode: existing acquisition audiences are set aside so that leaving AI mode restores them. */
export const startAiMode = (data) => {
    const builder = getBuilderState(data)
    if (builder.mode === 'ai') return data
    const acquisition = toArray(data?.audienceSelections?.acquisition)
    // Existing audiences that already run next to AI audiences stay selected
    if (builder.advanced) return { ...data, audienceBuilder: { ...builder, mode: 'ai' } }
    return {
        ...setAcquisition(data, []),
        audienceBuilder: { ...builder, mode: 'ai', stashedAcquisition: acquisition },
    }
}

/** Replaces the previous AI audiences with a generated set. Recommended audiences are selected. */
export const applyGeneratedSet = (data, result, inputs) => {
    const builder = getBuilderState(data)
    const map = {}
    toArray(result?.hypotheses).forEach((hypothesis) => {
        map[hypothesis.id] = {
            id: hypothesis.id,
            kind: hypothesis.kind,
            label: hypothesis.label,
            catalogueAudienceId: hypothesis.audience_id,
            audienceMode: hypothesis.audience_mode,
            rationale: hypothesis.rationale || null,
            interests: toArray(hypothesis.interests).map((interest) => ({ id: interest.id, name: interest.name })),
            ...withTargetingOptions(hypothesis.targeting_options),
            reachLower: hypothesis.estimated_reach_lower ?? null,
            reachUpper: hypothesis.estimated_reach_upper ?? null,
            unavailableTermCount: hypothesis.unavailable_term_count || 0,
            recommended: Boolean(hypothesis.recommended),
            basis: hypothesis.basis || null,
        }
    })
    const selected = Object.values(map)
        .filter((hypothesis) => hypothesis.recommended)
        .map((hypothesis) => hypothesis.id)
    const previousMap = getHypothesisMap(data)
    const legacy = builder.advanced ? withoutHypothesisIds(data?.audienceSelections?.acquisition, previousMap) : []

    return {
        ...setAcquisition(data, [...legacy, ...selected]),
        hypothesisAudiences: map,
        audienceBuilder: {
            ...builder,
            mode: 'ai',
            set: {
                id: result?.hypothesis_set_id || null,
                status: result?.status,
                reason: result?.reason || null,
                cached: Boolean(result?.cached),
                discardedPersonaCount: result?.discarded_persona_count || 0,
                plan: result?.plan || null,
                inputs,
            },
        },
    }
}

export const toggleHypothesis = (data, hypothesisId) => {
    const acquisition = toArray(data?.audienceSelections?.acquisition)
    const next = acquisition.includes(hypothesisId)
        ? acquisition.filter((id) => id !== hypothesisId)
        : [...acquisition, hypothesisId]
    return setAcquisition(data, next)
}

/**
 * Runs the existing Interest and Broad audiences next to the AI audiences (stored as `advanced`, also in older
 * drafts). Turning it on selects the existing audiences that were set aside again; turning it off sets the ones
 * still selected aside, so turning it on again brings them back.
 */
export const setExistingAudiencesMode = (data, enabled) => {
    const builder = getBuilderState(data)
    const map = getHypothesisMap(data)
    const acquisition = toArray(data?.audienceSelections?.acquisition)
    if (enabled) {
        const restored = withoutHypothesisIds(builder.stashedAcquisition, map)
        return {
            ...setAcquisition(data, [...new Set([...acquisition, ...restored])]),
            audienceBuilder: { ...builder, advanced: true, stashedAcquisition: [] },
        }
    }
    const setAside = withoutHypothesisIds(acquisition, map)
    return {
        ...setAcquisition(
            data,
            acquisition.filter((id) => map[id]),
        ),
        audienceBuilder: {
            ...builder,
            advanced: false,
            stashedAcquisition: [...new Set([...toArray(builder.stashedAcquisition), ...setAside])],
        },
    }
}

export const setBuilderField = (data, field, value) => ({
    ...data,
    audienceBuilder: { ...getBuilderState(data), [field]: value },
})

/** Back to the existing audience selection: AI audiences are dropped and the earlier selection is restored. */
export const leaveAiMode = (data) => {
    const builder = getBuilderState(data)
    const map = getHypothesisMap(data)
    const restored = withoutHypothesisIds(builder.stashedAcquisition, map)
    const keptLegacy = withoutHypothesisIds(data?.audienceSelections?.acquisition, map)
    const acquisition = [...new Set([...keptLegacy, ...restored])]
    return {
        ...setAcquisition({ ...data, hypothesisAudiences: {} }, acquisition),
        audienceBuilder: {
            ...builder,
            mode: 'manual',
            advanced: false,
            requestedPersonas: null,
            stashedAcquisition: [],
            basisSkip: false,
            set: null,
        },
    }
}

/** Audience objects for AI audiences, shaped like catalogue audiences so the existing screens can list them. */
export const hypothesisToAudience = (hypothesis) => ({
    id: hypothesis.id,
    name: hypothesis.label,
    description: hypothesis.rationale || '',
    icon: null,
    funnel_stage: 'acquisition',
    lookalike: false,
    ai: true,
    active: true,
    default: false,
    audience_hypothesis_id: hypothesis.id,
    hypothesis_kind: hypothesis.kind || null,
    audience_mode: hypothesis.audienceMode || null,
})

/** Catalogue audiences with the AI audiences of the wizard appended. Same array when there are none. */
export const mergeHypothesisAudiences = (audiences, hypothesisMap) => {
    const hypotheses = Object.values(hypothesisMap || {})
    if (hypotheses.length === 0) return audiences
    const base = audiences || []
    const known = new Set(base.map((audience) => audience.id))
    return [...base, ...hypotheses.filter((hypothesis) => !known.has(hypothesis.id)).map(hypothesisToAudience)]
}

export const isAiAudienceMode = (data) => getBuilderState(data).mode === 'ai'

/**
 * Applies a hypothesis to a funnel stage payload row: the catalogue audience id replaces the wizard's own id
 * and the hypothesis id is added. Rows of other audiences are returned unchanged.
 */
export const withHypothesisPayload = (row, hypothesisMap) => {
    const hypothesis = hypothesisMap?.[row.audience_id]
    if (!hypothesis) return row
    return { ...row, audience_id: hypothesis.catalogueAudienceId, audience_hypothesis_id: hypothesis.id }
}
